"use client";

import { memo, useMemo, useRef } from "react";
import { TableVirtuoso, type ItemProps } from "react-virtuoso";
import { Check, EyeOff, MessageCirclePlus, Plus, Share2, X } from "lucide-react";
import { VirtuosoErrorBoundary } from "@/components/virtuoso-error-boundary";
import { cn } from "@/lib/utils";
import type { HeldRows } from "@/hooks/use-order-hold";
import { resolveBetLink } from "@/lib/bet-links";
import type { DisplayPlayerProp, PlatformType } from "@/components/optimizer/utils-v2";
import { rowStakeQuote } from "@/components/optimizer/utils-v2/displayed-odds";
import { oddsAreBetterForBettor } from "@/components/optimizer/utils-v2/odds";
import type { SortDirection } from "@/components/optimizer/query-state";
import type { OpenOddsHistory } from "@/components/odds/odds-history-selection";
import type { Catalog, EventHeader } from "@/lib/odds-screen/live/types";
import { oddsScreenHrefForLiveRow } from "@/lib/fantasy-props/odds-screen-link";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { FantasyExpansion } from "./fantasy-expansion";
import { cellPrice, formatPct, rowLines, type BoardColumn } from "./model";
import { PlayerAvatar } from "@/components/ui/avatar";
import { playerTag } from "@/lib/fantasy-props/player-tag";
import { capText, PlayLineText, PromoIcon, promoMultiplier, PromoStrip } from "./promo";
import { RaceBadge } from "./race-panel";
import { BookHead, boardLayout, CELL, COL_W, ColumnHead, columnKey, columnsWidth, columnWidth, CopyName, HEAD, HIT_W, hitPillClass, PINNED, PINNED_HEAD, PINNED_SHADOW, PriceCell, ROW_ACTION, Scroller, TableEl, useClientWidth, WHALES_W, WhalesColumnCell, type BoardLayout } from "./board-parts";

export const HIT_COLUMN = "consensus_implied_probability";
export const FANTASY_LAYOUT = { playerW: 296 } as const;

type BoardItem = { readonly kind: "row" | "expansion"; readonly key: string };

export type BoardSlip = {
  readonly has: (rowId: string) => boolean;
  readonly toggle: (row: DisplayPlayerProp) => void;
};

type CellContext = {
  readonly platform: PlatformType;
  readonly columns: readonly BoardColumn[];
  readonly format: OddsFormat;
  readonly needPct: number | null;
  readonly slip: BoardSlip | null;
  readonly showLeague: boolean;
  readonly onShare: (row: DisplayPlayerProp) => void;
  readonly onChatShare: ((row: DisplayPlayerProp) => void) | null;
  readonly onHide: (row: DisplayPlayerProp) => void;
  readonly onRace: (() => void) | null;
  readonly isMobile: boolean;
  readonly layout: BoardLayout;
};

type BoardSort = { readonly column: string; readonly direction: SortDirection };

type BoardContext = {
  readonly cells: CellContext;
  readonly events: ReadonlyMap<string, EventHeader>;
  readonly expanded: string | null;
  readonly sort: BoardSort;
  readonly rows: HeldRows<DisplayPlayerProp>;
  readonly raceLegs: ReadonlyMap<string, number>;
  readonly onSort: (column: string, direction: SortDirection) => void;
  readonly onToggle: (key: string, row: DisplayPlayerProp) => void;
  readonly onOpenHistory: OpenOddsHistory;
  readonly catalog: Catalog | null;
  readonly tableWidth: number;
  readonly viewportWidth: number;
};

const NO_RACE_LEGS: ReadonlyMap<string, number> = new Map();

function TableRowEl({ item, context, ...props }: ItemProps<BoardItem> & { context?: BoardContext }) {
  if (item.kind === "expansion") return <tr {...props} className="bg-surface-raised" />;
  const row = context?.rows.rowAt(item.key) ?? null;
  if (row === null) return <tr {...props} />;
  const expanded = context?.expanded === item.key;
  const inSlip = context?.cells.slip?.has(item.key) ?? false;
  const toggle = () => context?.onToggle(item.key, row);
  return (
    <tr
      {...props}
      {...context?.rows.lapsedProps(item.key)}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${expanded ? "Collapse" : "Expand"} row details for ${row.player_name}`}
      onClick={toggle}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
      className={cn("group cursor-pointer focus-visible:outline-none", expanded ? "bg-white/[0.10]" : inSlip ? "bg-primary/[0.04] hover:bg-primary/[0.06]" : "hover:bg-white/[0.02] focus-visible:bg-white/[0.03]")}
    />
  );
}

const HeaderRow = memo(function HeaderRow({ cells, sort, onSort }: { cells: CellContext; sort: BoardSort; onSort: BoardContext["onSort"] }) {
  const { platform, columns, needPct, isMobile, layout: { playerW, bookW, pinStats } } = cells;
  const hitSorted = sort.column === HIT_COLUMN;
  const need = needPct !== null ? `need ${formatPct(needPct)}` : null;
  return (
    <tr className="h-16">
      <th className={cn("sticky left-0 z-[30] text-left", HEAD, pinStats ? undefined : PINNED_SHADOW)} style={{ width: playerW }}>
        <span className="flex h-full items-center pl-4 text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Play</span>
      </th>
      <th className={cn(pinStats && PINNED_HEAD, HEAD)} style={{ left: playerW, width: bookW }}>
        <BookHead book={platform} />
      </th>
      <th aria-sort={hitSorted ? (sort.direction === "asc" ? "ascending" : "descending") : "none"} className={cn("text-left", HEAD, pinStats && PINNED_HEAD, pinStats && PINNED_SHADOW)} style={{ left: playerW + bookW, width: HIT_W }}>
        <button
          type="button"
          onClick={() => onSort(HIT_COLUMN, hitSorted && sort.direction === "desc" ? "asc" : "desc")}
          aria-label={`Sort by odds to hit, ${hitSorted ? (sort.direction === "asc" ? "ascending" : "descending") : "not sorted"}`}
          className="flex h-full w-full flex-col items-start justify-center px-3 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className={cn("flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.06em]", hitSorted ? "text-foreground" : "text-zinc-500")}>
            To hit
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={cn("transition-transform", hitSorted && sort.direction === "asc" && "rotate-180", !hitSorted && "opacity-40")}><path d="m6 9 6 6 6-6" /></svg>
          </span>
          {need !== null && <span key={need} className="data-num text-[10px] text-zinc-500 animate-in fade-in-0 slide-in-from-bottom-1 duration-300 motion-reduce:animate-none">{need}</span>}
        </button>
      </th>
      {columns.map((column) => (
        <th key={columnKey(column)} className={HEAD} style={{ width: columnWidth(column) }}>
          <ColumnHead column={column} tap={isMobile} />
        </th>
      ))}
      <th className={HEAD} aria-hidden="true" />
    </tr>
  );
});

const RowCells = memo(function RowCells({ rowKey, row, event, isExpanded, raceBoost, cells }: {
  rowKey: string;
  row: DisplayPlayerProp;
  event: EventHeader | undefined;
  isExpanded: boolean;
  raceBoost: number | null;
  cells: CellContext;
}) {
  const { platform, columns, format, onShare, onChatShare, onHide, onRace, needPct, slip, showLeague, isMobile, layout: { playerW, bookW, pinStats } } = cells;
  const inSlip = slip?.has(rowKey) ?? false;
  const lines = rowLines(row, event);
  const primaryLink = resolveBetLink(row.__comparison_links?.[platform], isMobile) ?? row.bet_link ?? null;
  const hit = typeof row.odds_to_hit === "number" && Number.isFinite(row.odds_to_hit) ? row.odds_to_hit : null;
  const breakeven = row.effective_slip_breakeven_pct ?? needPct;
  return (
    <>
      <td className={cn(PINNED, CELL, "left-0 border-l-2", isExpanded ? "border-l-primary" : "border-l-transparent", !pinStats && PINNED_SHADOW)} style={{ width: playerW }}>
        <div className="flex items-center gap-2 py-1.5 pl-3 pr-2">
          <PlayerAvatar name={lines.title} imageUrl={row.player_image_url ?? null} size={32} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="line-clamp-2 text-[11px] leading-4 text-zinc-500">
              {showLeague && row.league && <span className="mr-1.5 inline-block rounded bg-white/[0.06] px-1 align-[1px] text-[10px] font-semibold leading-[14px] tracking-[0.04em] text-zinc-400">{row.league}</span>}
              {lines.matchup}
              {lines.time && <span className="whitespace-nowrap"> · {lines.time}</span>}
            </span>
            <span className="flex items-center gap-1">
              <span className="line-clamp-2 min-w-0 text-[13px] font-semibold leading-[18px] text-foreground [overflow-wrap:anywhere]">{lines.title}</span>
              {playerTag(row) && <span className="min-w-0 shrink-[999] truncate text-[11px] font-medium leading-[18px] text-zinc-500">{playerTag(row)}</span>}
              {row.promo != null && <PromoIcon promo={row.promo} />}
              {raceBoost !== null && onRace !== null && <RaceBadge boost={raceBoost} onOpen={onRace} />}
              <CopyName name={lines.title} />
              {inSlip && <Check size={12} strokeWidth={3} className="flex-shrink-0 text-primary" aria-label="In your slip" role="img" />}
            </span>
            <PlayLineText play={lines.play} className="line-clamp-2 text-[13px] leading-[18px] text-zinc-300 [overflow-wrap:anywhere]" />
          </div>
          <div className={cn("flex flex-shrink-0 flex-col items-center", isMobile || isExpanded ? "opacity-100" : "opacity-0 focus-within:opacity-100 group-hover:opacity-100")}>
            {slip !== null && (
              <button
                type="button"
                aria-label={inSlip ? `Remove ${row.player_name} from slip` : `Add ${row.player_name} to slip`}
                onClick={(event) => { event.stopPropagation(); slip.toggle(row); }}
                className={ROW_ACTION}
              >
                {inSlip ? <X size={11} aria-hidden="true" /> : <Plus size={11} aria-hidden="true" />}
              </button>
            )}
            <button
              type="button"
              aria-label="Share this pick"
              onClick={(event) => { event.stopPropagation(); onShare(row); }}
              className={ROW_ACTION}
            >
              <Share2 size={11} aria-hidden="true" />
            </button>
            {onChatShare && (
              <button type="button" aria-label={`Share ${row.player_name} to chat`} title="Share to chat" onClick={(event) => { event.stopPropagation(); onChatShare(row); }} className={ROW_ACTION}>
                <MessageCirclePlus size={11} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              aria-label={`Hide ${row.player_name}`}
              onClick={(event) => { event.stopPropagation(); onHide(row); }}
              className={ROW_ACTION}
            >
              <EyeOff size={11} aria-hidden="true" />
            </button>
          </div>
        </div>
      </td>
      <td className={cn(pinStats && PINNED, CELL)} style={{ left: playerW, width: bookW }}>
        <PriceCell row={row} book={platform} odds={row.primary_odds ?? null} line={null} format={format} link={primaryLink} note={capText(row.promo?.cap ?? null)} stake={rowStakeQuote(row, platform, row.primary_odds ?? null)} mult={promoMultiplier(row)} primary />
      </td>
      <td className={cn(pinStats && PINNED, CELL, pinStats && PINNED_SHADOW)} style={{ left: playerW + bookW, width: HIT_W }}>
        <div className="flex items-center px-3">
          {hit === null ? (
            <span className="text-[13px] text-zinc-600">–</span>
          ) : (
            <span className={hitPillClass(hit, breakeven)}>{hit.toFixed(1)}%</span>
          )}
        </div>
      </td>
      {columns.map((column) => {
        if (column.kind === "whales") {
          return (
            <td key="whales" className={CELL} style={{ width: WHALES_W }}>
              <WhalesColumnCell row={row} format={format} isMobile={isMobile} />
            </td>
          );
        }
        const { book } = column;
        const price = cellPrice(row, book);
        const baseline = typeof row.primary_odds === "number" ? row.primary_odds : null;
        const better = price.line === null && price.odds !== null && baseline !== null && oddsAreBetterForBettor(price.odds, baseline);
        return (
          <td key={book} className={CELL} style={{ width: COL_W }}>
            <PriceCell row={row} book={book} odds={price.odds} line={price.line} format={format} link={resolveBetLink(row.__comparison_links?.[book], isMobile)} stake={price.stake} better={better} />
          </td>
        );
      })}
      <td className={CELL} aria-hidden="true" />
    </>
  );
});

function ExpansionCell({ row, context }: { row: DisplayPlayerProp; context: BoardContext }) {
  const { platform, columns, format, isMobile } = context.cells;
  return (
    <td colSpan={3 + columns.length + 1} className={cn(CELL, "p-0")}>
      <div className="sticky left-0" style={{ width: context.viewportWidth || undefined }}>
        {row.promo != null && <PromoStrip promo={row.promo} className="border-l-2 border-l-primary" />}
        <FantasyExpansion row={row} platform={platform} columns={columns} format={format} onOpenHistory={context.onOpenHistory} oddsScreenHref={oddsScreenHrefForLiveRow(context.catalog, row)} isMobile={isMobile} />
      </div>
    </td>
  );
}

export type FantasyBoardProps = {
  rows: HeldRows<DisplayPlayerProp>;
  events: ReadonlyMap<string, EventHeader>;
  platform: PlatformType;
  columns: readonly BoardColumn[];
  format: OddsFormat;
  expanded: string | null;
  sort: { readonly column: string; readonly direction: SortDirection };
  needPct: number | null;
  slip?: BoardSlip | null;
  showLeague?: boolean;
  onSort: (column: string, direction: SortDirection) => void;
  onToggle: (key: string, row: DisplayPlayerProp) => void;
  onShare: (row: DisplayPlayerProp) => void;
  onChatShare?: ((row: DisplayPlayerProp) => void) | null;
  onHide: (row: DisplayPlayerProp) => void;
  onOpenHistory: OpenOddsHistory;
  catalog: Catalog | null;
  isMobile: boolean;
  raceLegs?: ReadonlyMap<string, number>;
  onRace?: (() => void) | null;
};

export function FantasyBoard({
  rows, events, platform, columns, format, expanded, sort, needPct, slip = null, showLeague = false, onSort, onToggle, onShare, onChatShare = null, onHide, onOpenHistory, catalog, isMobile,
  raceLegs = NO_RACE_LEGS, onRace = null,
}: FantasyBoardProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewportWidth = useClientWidth(hostRef);

  const items = useMemo<BoardItem[]>(() => {
    const out: BoardItem[] = [];
    for (const key of rows.ids) {
      out.push({ kind: "row", key });
      if (expanded === key) out.push({ kind: "expansion", key });
    }
    return out;
  }, [rows, expanded]);

  const { playerW, bookW, pinStats } = boardLayout(viewportWidth, HIT_W, HIT_W, columns, FANTASY_LAYOUT);
  const layout = useMemo<BoardLayout>(() => ({ playerW, bookW, pinStats }), [playerW, bookW, pinStats]);
  const cells = useMemo<CellContext>(
    () => ({ platform, columns, format, needPct, slip, showLeague, onShare, onChatShare, onHide, onRace, isMobile, layout }),
    [platform, columns, format, needPct, slip, showLeague, onShare, onChatShare, onHide, onRace, isMobile, layout],
  );
  const { column: sortColumn, direction: sortDirection } = sort;
  const boardSort = useMemo<BoardSort>(() => ({ column: sortColumn, direction: sortDirection }), [sortColumn, sortDirection]);
  const context = useMemo<BoardContext>(() => ({
    cells, events, expanded, sort: boardSort, rows, raceLegs, onSort, onToggle, onOpenHistory, catalog,
    tableWidth: playerW + bookW + HIT_W + columnsWidth(columns),
    viewportWidth,
  }), [cells, events, expanded, boardSort, rows, raceLegs, onSort, onToggle, onOpenHistory, catalog, playerW, bookW, columns, viewportWidth]);
  const components = useMemo(() => ({ Scroller, Table: TableEl, TableRow: TableRowEl }), []);

  return (
    <div ref={hostRef} className="relative min-h-0 flex-grow">
      <VirtuosoErrorBoundary>
        <TableVirtuoso
          style={{ height: "100%", width: "100%" }}
          {...rows.list}
          data={items}
          context={context}
          components={components}
          computeItemKey={(_, item) => (item.kind === "row" ? item.key : `expansion:${item.key}`)}
          fixedHeaderContent={() => <HeaderRow cells={context.cells} sort={context.sort} onSort={context.onSort} />}
          itemContent={(_, item, ctx) => {
            const row = ctx.rows.rowAt(item.key);
            if (row === null) return null;
            if (item.kind === "expansion") return <ExpansionCell row={row} context={ctx} />;
            return (
              <RowCells
                rowKey={item.key}
                row={row}
                event={row.event_id ? ctx.events.get(row.event_id) : undefined}
                isExpanded={ctx.expanded === item.key}
                raceBoost={ctx.raceLegs.get(item.key) ?? null}
                cells={ctx.cells}
              />
            );
          }}
        />
      </VirtuosoErrorBoundary>
    </div>
  );
}
